// Shared helpers for rendering an order's applied promotions.
//
// `applied_promotions` (the raw applied_promotions_json) has THREE shapes in
// the wild: a bare LIST (legacy + pre-#83 manual orders), the pending envelope
// {_pending, usages} written at checkout, and the paid envelope
// {applied_at, promotions} written on PAID. Manual orders now write BOTH keys,
// so prefer `promotions` (it carries name + code) — but only when POPULATED:
// `raw?.promotions || raw?.usages` would pick an empty array over a full one.
//
// Free shipping is folded straight into order_total by the backend and never
// persisted as a column — shipping_total stays GROSS and total_discount counts
// ITEM discounts only. Every surface that renders an order breakdown needs the
// shippingDiscountOf line or a free-shipping order shows Subtotal + gross
// Shipping with an unexplained gap at the Total (the r-review BLOCKER,
// 2026-08-25). Was three per-page copies; hoisted when ShipmentDrawer became
// the fourth surface (Cowork Run 27 C2).

const rowsOf = (raw) => (Array.isArray(raw)
  ? raw
  : (raw?.usages?.length ? raw.usages : (raw?.promotions || [])));

const isShippingRow = (u) => u.type === 'shipping'
  || u.type === 'SHIPPING_DISCOUNT'
  || u.promotion_type === 'SHIPPING_DISCOUNT';

export const shippingDiscountOf = (orderData) => {
  return rowsOf(orderData?.applied_promotions)
    .filter(isShippingRow)
    .reduce((sum, u) => sum + (parseFloat(u.discount_amount ?? u.discount) || 0), 0);
};

export const shippingOfferName = (orderData) => {
  const ship = rowsOf(orderData?.applied_promotions).find(isShippingRow);
  return ship ? (ship.promotion_name || ship.name || null) : null;
};

export const namedPromotions = (raw) => {
  const rows = Array.isArray(raw)
    ? raw
    : (raw?.promotions?.length
      ? raw.promotions
      : (raw?.usages?.length ? raw.usages : []));
  return rows
    // Shipping offers discount SHIPPING, not the amounts in these rows —
    // naming them here would credit money this breakdown doesn't hold.
    // ('type' is the usage lane in the pending shape, the PromotionType in
    // the paid one.)
    .filter((p) => p && !isShippingRow(p))
    .map((p) => ({
      name: p.name || p.promotion_name || null,
      // Dollars in discount_amount (string when pending, float when paid)
      // and in `discount`; CENTS in `amount`.
      amount: p.discount_amount != null
        ? parseFloat(p.discount_amount)
        : (p.discount != null
          ? parseFloat(p.discount)
          : (typeof p.amount === 'number' ? p.amount / 100 : null)),
    }))
    // Degrade gracefully: an unnamed row would render as a blank bullet, so
    // drop it and let the page keep its plain rows.
    .filter((p) => p.name);
};
